import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useListDocumentTypes } from '@/document-types/application/hooks/useListDocumentTypes';
import type { DocumentType } from '@/document-types/domain/types/DocumentType';

void React;

export interface DocumentTypesProviderProps {
  /**
   * Optional whitelist. If provided, the context exposes only doc types whose
   * `documentTypeKey` appears here, preserving the BE response order.
   *
   * If empty array or undefined, all fetched types are exposed (current behavior).
   */
  allowedDocumentTypeKeys?: string[];
  /**
   * Optional default `documentTypeKey`.
   *
   * When provided and present in the (filtered) list, the provider exposes it as
   * `defaultType` so callers can pre-select.
   *
   * When not present, falls back to the first item.
   *
   * When not provided, CRM-legacy behavior applies: pick the type whose
   * `displayName === 'Quote'`, else the first item.
   */
  defaultDocumentTypeKey?: string;
}

interface DocumentTypesContextValue {
  types: DocumentType[];
  isLoading: boolean;
  error: unknown;
  defaultType: DocumentType | undefined;
}

const DocumentTypesContext = createContext<DocumentTypesContextValue | null>(null);

export const DocumentTypesProvider = ({
  children,
  allowedDocumentTypeKeys,
  defaultDocumentTypeKey
}: PropsWithChildren<DocumentTypesProviderProps>) => {
  const { data = [], isLoading, error } = useListDocumentTypes();

  const value = useMemo<DocumentTypesContextValue>(() => {
    const filtered =
      allowedDocumentTypeKeys && allowedDocumentTypeKeys.length > 0
        ? data.filter((t) => allowedDocumentTypeKeys.includes(t.documentTypeKey))
        : data;

    const fromHost = defaultDocumentTypeKey ? filtered.find((t) => t.documentTypeKey === defaultDocumentTypeKey) : undefined;

    // Backwards compatibility: legacy CRM default was "find Quote, else first".
    const legacy = filtered.find((t) => t.displayName === 'Quote') ?? filtered[0];

    return {
      types: filtered,
      isLoading,
      error,
      defaultType: fromHost ?? legacy
    };
  }, [allowedDocumentTypeKeys, data, defaultDocumentTypeKey, error, isLoading]);

  return <DocumentTypesContext.Provider value={value}>{children}</DocumentTypesContext.Provider>;
};

export const useDocumentTypes = (): DocumentTypesContextValue => {
  const ctx = useContext(DocumentTypesContext);
  if (!ctx) throw new Error('useDocumentTypes must be used inside <DocumentTypesProvider>');
  return ctx;
};

/**
 * `useDocumentTypes` for a surface that works without a provider (the host mounts the Block Library
 * with or without one): `null` when there is none, instead of throwing.
 */
export const useOptionalDocumentTypes = (): DocumentTypesContextValue | null => useContext(DocumentTypesContext);
